import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  fetchMedication,
  updateMedication,
  fetchStockAdjustments,
  addStockAdjustment,
  fetchMedicationPrescriptions,
  clearMedication,
} from '../store/medicationsSlice'
import type { Medication } from '../store/medicationsSlice'
import { MedicationDetailHeader } from '../components/medications/MedicationDetailHeader'
import { MedicationDetails } from '../components/medications/MedicationDetails'
import { StockManagement } from '../components/medications/StockManagement'
import { MedicationPrescriptions } from '../components/medications/MedicationPrescriptions'

export function MedicationDetailPage() {
  const { medicationId } = useParams<{ medicationId: string }>()
  const dispatch = useAppDispatch()
  const { medication, stockAdjustments, prescriptions, loading } = useAppSelector((s) => s.medications)

  useEffect(() => {
    if (medicationId) {
      dispatch(fetchMedication(medicationId))
      dispatch(fetchStockAdjustments(medicationId))
      dispatch(fetchMedicationPrescriptions(medicationId))
    }
    return () => {
      dispatch(clearMedication())
    }
  }, [dispatch, medicationId])

  const handleUpdate = async (data: Partial<Medication>) => {
    if (!medicationId) return
    await dispatch(updateMedication({ id: medicationId, data }))
  }

  const handleAdjustStock = async (data: { medication_id: string; adjustment: number; new_quantity: number; reason: string; adjusted_by: string }) => {
    await dispatch(addStockAdjustment(data))
    // If it's a controlled substance, auto-create a controlled substance log entry
    if (medication?.controlled_substance) {
      try {
        await fetch('/.netlify/functions/controlled-substances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            medication_id: data.medication_id,
            transaction_type: data.adjustment > 0 ? 'Received' : 'Adjustment',
            quantity: Math.abs(data.adjustment),
            performed_by: data.adjusted_by,
            notes: data.reason,
          }),
        })
      } catch {
        // Non-critical: log entry creation failure shouldn't block stock adjustment
      }
    }
  }

  if (loading || !medication) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="medication-detail-page">
        <p className="text-[13px] text-text-muted">Loading medication...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="medication-detail-page">
      <MedicationDetailHeader medication={medication} onUpdate={handleUpdate} />

      <div className="flex flex-col gap-4 mt-4">
        <MedicationDetails medication={medication} />
        <StockManagement
          medication={medication}
          adjustments={stockAdjustments}
          onAdjustStock={handleAdjustStock}
        />
        <MedicationPrescriptions prescriptions={prescriptions} />
      </div>
    </div>
  )
}
